import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToolTip from "../../components/component-tools/ToolTip";
import { ReferenceDetailContactInfo } from "../../interfaces/referenceDetaill";
import {
  getDataForIncompleteTable,
  getReferenceDetailByID,
  updateReferenceDetail,
} from "../../services/referenceDetailService";
import { generateCertifiedMail } from "../../services/reportService";
import SendLetter from "../../components/order-components/SendLetter";
import SendBatchLetters from "../../components/order-components/SendBatchLetters";
import SendBatchCertifiedLetters from "../../components/order-components/SendBatchCertifiedLetters";
import { formatDate } from "../../services/helpers";

const ReferenceTracking = () => {
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [references, setReferences] = useState<
    ReferenceDetailContactInfo[] | null
  >([]);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showSendLetterModal, setShowSendLetterModal] = useState(false);
  const [showSendBatchLettersModal, setShowSendBatchLettersModal] =
    useState(false);
  const [
    showSendBatchCertifiedLettersModal,
    setShowSendBatchCertifiedLettersModal,
  ] = useState(false);
  const [selectedReferenceDetailID, setSelectedReferenceDetailID] = useState<
    number | null
  >(null);

  useEffect(() => {
    const fetchInvoicingData = async () => {
      try {
        setLoading(true);
        const referencesResponse = await getDataForIncompleteTable();
        if (referencesResponse) {
          const updatedReferences = referencesResponse.map((ref) => ({
            ...ref,
            certified:
              ref.nextContactMethod === "Certified Mail" && !ref.entityRed,
            includeInSendLetter: false,
          }));
          setReferences(updatedReferences);
        }
      } catch {
        console.log("error fetching references");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoicingData();
  }, []);

  const handleGenerateCertifiedMailCSV = async () => {
    try {
      setLoadingSpinner(true);

      const certifiedIDs =
        references
          ?.filter((ref) => !!ref.certified)
          .map((ref) => ref.referenceDetailID) ?? [];

      const response = await generateCertifiedMail(certifiedIDs);
      if (!response) throw new Error("No response from server");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const formattedDate = `${
        today.getMonth() + 1
      }-${today.getDate()}-${today.getFullYear()}`;
      const fileName = `Certified_Mail_${formattedDate}.csv`;

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error) {
      console.error("Error generating report:", error);
      setErrorMessage("Error generating incomplete status report.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index: number,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (references && references[index] && field in references[index]) {
      setReferences((prevList) => {
        if (!prevList) return prevList;
        const updatedList = [...prevList];
        updatedList[index] = { ...updatedList[index], [field]: value };
        return updatedList;
      });
    }
  };

  const handleHideReference = async (referenceDetailID: number) => {
    try {
      setLoadingSpinner(true);

      var referenceDetail = await getReferenceDetailByID(referenceDetailID);
      referenceDetail.followUp = false;

      await updateReferenceDetail(referenceDetail);

      window.location.reload();
    } catch (error) {
      console.error("Error generating report:", error);
      setErrorMessage("Error generating incomplete status report.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Reference Tracking
          <div
            onClick={() => setShowNotesModal(true)}
            style={{ cursor: "pointer", display: "flex" }}
          >
            <ToolTip
              tooltipText="Click to view details about this table"
              placement="right"
              className="info-icon"
            />
          </div>
        </h1>
        <div className="page-items-container">
          <div style={{ cursor: "pointer", display: "flex" }}>
            <button
              className="oxbtn btn-grey"
              onClick={() => setShowSendBatchCertifiedLettersModal(true)}
            >
              Send Certified Mail
            </button>
            <div>
              <ToolTip
                tooltipText="Send batch MAIL to references and email candidates selected in “CSV” column. Process Certified Mail CSV first. "
                placement="right"
                className="info-icon"
              />
            </div>
          </div>
          <div style={{ cursor: "pointer", display: "flex" }}>
            <button
              className="oxbtn btn-grey"
              onClick={() => setShowSendBatchLettersModal(true)}
            >
              Send Letters
            </button>
            <div>
              <ToolTip
                tooltipText="Send batch EMAIL to references and candidates selected in “Send Letter” column"
                placement="right"
                className="info-icon"
              />
            </div>
          </div>
          <div style={{ cursor: "pointer", display: "flex" }}>
            <button
              className="oxbtn btn-grey"
              onClick={() => handleGenerateCertifiedMailCSV()}
            >
              Certified Mail CSV
            </button>
            <div>
              <ToolTip
                tooltipText="Generates a CSV file for certified mail processing"
                placement="left"
                className="info-icon"
              />
            </div>
          </div>
        </div>
      </div>
      {showSendBatchLettersModal && (
        <SendBatchLetters
          references={references}
          onClose={() => {
            setShowSendBatchLettersModal(false);
          }}
          onSuccess={() => window.location.reload()}
        />
      )}
      {showSendBatchCertifiedLettersModal && (
        <SendBatchCertifiedLetters
          references={references}
          onClose={() => {
            setShowSendBatchCertifiedLettersModal(false);
          }}
          onSuccess={() => window.location.reload()}
        />
      )}

      {showNotesModal && (
        <div className="modal-mask">
          <div className="modal-content">
            <ul style={{ marginTop: "10px", paddingLeft: "20px" }}>
              <li>
                This table displays all in-process references that require a
                follow-up based on our standard contact schedule.
              </li>
              <li>
                A follow-up is due if it's been:
                <ul>
                  <li>
                    <strong>7+ days</strong> since last email (for
                    email-initiated contacts)
                  </li>
                  <li>
                    <strong>14+ days</strong> since initial email (for
                    email-initiated contacts)
                  </li>
                  <li>
                    <strong>14+ days</strong> since last mail (for
                    mail-initiated contacts)
                  </li>
                  <li>
                    <strong>Less than 28 days</strong> since initial contact
                  </li>
                </ul>
              </li>
              <li>
                Only references with no response received, initial letters sent
                and no reference final report date are shown.
              </li>
              <li>
                Contact history shows the date and method of each attempt:
                <strong> (E)</strong> Email, <strong>(M)</strong> Mail,{" "}
                <strong>(CM)</strong> Certified Mail.
              </li>
              <li>
                The "Follow Up" column recommends the next contact method based
                on the contact history and timing rules.
                <ul>
                  <li>
                    <strong>
                      Check the checkbox to include that reference in the
                      certified mail csv **
                    </strong>
                  </li>
                </ul>
              </li>
              <li>
                If a reference hasn't responded within 28 days of initial
                contact the corresponding order will be visible in the close
                order table.
              </li>
              <li>
                Clicking hide manages visibility settings for the table. You can
                un-hide a reference in the reference detail page.
              </li>
            </ul>
            <div>
              <button
                className="smbtn btn-red"
                onClick={() => setShowNotesModal(false)}
                style={{ marginBottom: "15px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container">
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>Last</th>
                    <th style={{ width: "60px" }}></th>
                    <th style={{ width: "180px" }}>Candidate Name</th>
                    <th style={{ width: "180px" }}>Entity Name</th>
                    <th style={{ width: "130px" }}>Contact Dates</th>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "100px" }}>Follow Up</th>
                    <th style={{ width: "50px" }}>CSV</th>
                    <th style={{ width: "80px" }}>Send Letter</th>
                    <th style={{ width: "150px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {references?.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: "center" }}>
                        Nothing to follow up on.
                      </td>
                    </tr>
                  ) : (
                    references?.map((reference, index) => {
                      return (
                        <tr
                          key={index}
                          onClick={() =>
                            navigate(
                              `/admin/add-reference/${reference.referenceDetailID}`,
                              {
                                state: {
                                  referenceDetailID:
                                    reference.referenceDetailID,
                                },
                              },
                            )
                          }
                        >
                          <td>
                            <div className="error">
                              {reference.daysSinceLastContact}
                            </div>
                          </td>
                          <td>
                            <button
                              className={`smbtn btn-grey`}
                              style={{ padding: "5px 10px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHideReference(
                                  reference.referenceDetailID,
                                );
                              }}
                            >
                              Hide
                            </button>
                          </td>
                          <td>{reference.candidateName}</td>
                          <td
                          // style={
                          //   reference.entityRed
                          //     ? { color: "var(--danger)" }
                          //     : {}
                          // }
                          >
                            {reference.entityName}
                          </td>
                          <td>{reference.contactSummary.join(", ")}</td>
                          <td>
                            {reference?.alert?.trim() && (
                              <ToolTip
                                tooltipText={`${reference.alert}\n${formatDate(reference?.alertLastUpdatedDate, "datetime")}`}
                                placement="top"
                                className="alert-sticky"
                              >
                                <i className="bi bi-stickies-fill"></i>
                              </ToolTip>
                            )}
                          </td>
                          <td>
                            <span
                              className={
                                reference.nextContactMethod === "Certified Mail"
                                  ? "badge orange-chip"
                                  : "badge blue-chip"
                              }
                            >
                              {reference.nextContactMethod}
                            </span>
                          </td>
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            {!reference.entityRed && (
                              <div>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={reference.certified === true}
                                  onChange={(e) =>
                                    handleInputChange(
                                      e as React.ChangeEvent<HTMLInputElement>,
                                      "certified",
                                      index,
                                    )
                                  }
                                />
                              </div>
                            )}
                          </td>
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={reference.includeInSendLetter === true}
                                onChange={(e) =>
                                  handleInputChange(
                                    e as React.ChangeEvent<HTMLInputElement>,
                                    "includeInSendLetter",
                                    index,
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className={`smbtn btn-grey`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReferenceDetailID(
                                  reference.referenceDetailID,
                                );
                                setShowSendLetterModal(true);
                              }}
                            >
                              Send Letter
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {showSendLetterModal && selectedReferenceDetailID !== null && (
        <SendLetter
          referenceDetailID={selectedReferenceDetailID}
          onClose={() => {
            setShowSendLetterModal(false);
            setSelectedReferenceDetailID(null);
          }}
          onSuccess={() => window.location.reload()}
        />
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ReferenceTracking;
